import { useState, useEffect, useCallback, Key } from 'react';
import { RowsChangeData } from '@bst/react-data-grid';
import { InvitationStrategy, UserDTO } from '../../../apiclients/bstinsights';
import { useInviteUser } from './useInviteUser';
import { useUpdateUser } from './useUpdateUser';
import { useGetUsersPaged } from './useGetUsersPaged';
import { useGridPreferences } from './useGridPreferences';
import { useRealTimeUserUpdate } from './useRealTimeUserUpdate';
import { useStores } from '../../../stores/useStores';

export const useUserManagement = () => {
  const { sort } = useGridPreferences();
  const { userManagementStore } = useStores()
  const {
    mutateAsync: inviteUser,
    isPending: inviteUsersIsPending
  } = useInviteUser();

  const { mutate: updateUser } = useUpdateUser();

  // Retrieve data from the infinite query. (data is a flattened array of UserDTO.)
  const {
    data: queryData = [],
    fetchNextPage,
    hasNextPage,
    isFetching,
    isRefetching
  } = useGetUsersPaged({
    sort,
    enabled: !inviteUsersIsPending,
    filters: userManagementStore.getGridFilters()
  });

  // Create local state for the currently displayed users.
  const [users, setUsers] = useState<UserDTO[]>(queryData);

  // Update local state when the query data changes.
  useEffect(() => {
    setUsers(queryData);
  }, [queryData]);

  // Now call the invitation status hook with the local state and the updater.
  useRealTimeUserUpdate(users, setUsers);

  const handleInviteUser = useCallback(async (
    selected: Key[],
    invitationStrategy: InvitationStrategy,
    onSuccess: () => void
  ): Promise<number[] | undefined> => {
    const usersToInvite = users.filter((u) => selected.includes(u.userId!));
    return await inviteUser(
      { users: usersToInvite, invitationStrategy, selected },
      {
        onSuccess,
      },
    );
  }, [users, inviteUser]);

  const handleRowsChange = useCallback((rows: UserDTO[], data: RowsChangeData<UserDTO>) => {
    const changed = data.indexes.map((i) => rows[i]);
    const changedIds = changed.map((item) => item.userId);
    const user = rows.find((u) => changedIds.includes(u.userId));

    if (user) {
      updateUser({ user });
    }
  }, [updateUser]);

  const loadNextPage = async () => {
    if (!isFetching && hasNextPage) {
      await fetchNextPage();
    }
  };

  return {
    data: users, // local state now reflects the current users data
    isFetching,
    isRefetching,
    loadNextPage,
    handleInviteUser,
    handleRowsChange
  };
};
